import { SearchIcon, XIcon } from "lucide-react";
import {
  composeRenderProps,
  SearchField as RACSearchField,
  type SearchFieldProps as RACSearchFieldProps,
} from "react-aria-components";
import { tv } from "tailwind-variants";

import { Button } from "@/components/ui/button";
import { Input, type InputProps } from "@/components/ui/field";
import { composeTailwindRenderProps } from "@/components/ui/utils";

const searchFieldStyles = tv({
  base: "group relative flex min-w-10 gap-1",
  slots: {
    searchIcon:
      "ml-2 size-4 shrink-0 text-muted-foreground group-disabled:text-muted-foreground/50 forced-colors:text-[ButtonText] forced-colors:group-disabled:text-[GrayText]",
    closeButton:
      "mr-1 size-8 text-muted-foreground group-empty:invisible hover:bg-transparent pressed:text-foreground",
    input: "[&::-webkit-search-cancel-button]:hidden peer pe-9 ps-9",
  },
});

const { base, searchIcon, closeButton, input } = searchFieldStyles();

export function SearchField(props: RACSearchFieldProps) {
  return (
    <RACSearchField
      {...props}
      className={composeTailwindRenderProps(props.className, base())}
    >
      {composeRenderProps(props.children, (children) => (
        <>
          {children}
          <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-1 text-muted-foreground/80 peer-disabled:opacity-50">
            <SearchIcon data-slot="icon" aria-hidden className={searchIcon()} />
          </div>
          <Button
            size="icon"
            variant="ghost"
            className={closeButton({
              className: "absolute inset-y-0 end-0",
            })}
          >
            <XIcon aria-hidden />
          </Button>
        </>
      ))}
    </RACSearchField>
  );
}

export function SearchInput({
  placeholder = "Search...",
  ...props
}: InputProps) {
  return (
    <Input
      {...props}
      className={composeTailwindRenderProps(props.className, input())}
      type="search"
      placeholder={placeholder}
    />
  );
}
